import { RefreshCwIcon } from 'lucide-react';
import type { EndpointAnalytics } from '../../hooks';
import { Skeleton } from '../Skeleton';
import { methodColor, statusColor } from '../../utils/httpColors';

interface AnalyticsDashboardProps {
  analytics: EndpointAnalytics | null;
  loading: boolean;
  onRefresh: () => void;
}

function getStatusLabel(status: number): string {
  if (status >= 200 && status < 300) return 'Success';
  if (status >= 300 && status < 400) return 'Redirect';
  if (status >= 400 && status < 500) return 'Client Error';
  if (status >= 500) return 'Server Error';
  return 'Unknown';
}

function TrendArrow({ today, yesterday }: { today: number; yesterday: number }) {
  if (yesterday === 0 && today === 0) return <span className="text-base-content/70 text-xs">No recent activity</span>;
  if (yesterday === 0) return <span className="text-success text-xs">New today</span>;

  const pctChange = Math.round(((today - yesterday) / yesterday) * 100);
  if (pctChange === 0) return <span className="text-base-content/70 text-xs">Same as yesterday</span>;

  return (
    <span className={`text-xs ${pctChange > 0 ? 'text-success' : 'text-error'}`}>
      {pctChange > 0 ? '+' : ''}{pctChange}% vs yesterday
    </span>
  );
}

function BarChart({ data }: { data: Array<{ timestamp: string; count: number }> }) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-base-content/70 text-sm">
        No request data in the last 24 hours
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);

  // Scrolls on narrow screens rather than squeezing 24 bars to slivers. Only
  // below sm: a scroll container would also clip the hover tooltips.
  return (
    <div className="overflow-x-auto sm:overflow-visible">
      <div className="flex gap-2 min-w-[28rem] sm:min-w-0">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between text-[10px] text-base-content/70 py-0.5 w-6 text-right shrink-0">
          <span>{maxCount}</span>
          <span>{Math.round(maxCount / 2) || ''}</span>
          <span>0</span>
        </div>
  
        <div className="flex-1 flex flex-col">
          {/* Bars */}
          <div className="flex items-end gap-px h-40">
            {data.map((d, i) => {
              const height = d.count === 0 ? 0 : Math.max((d.count / maxCount) * 100, 4);
              const hour = new Date(d.timestamp).getHours();
              const isNow = i === data.length - 1;
              return (
                <div
                  key={d.timestamp}
                  className="group relative flex-1 flex flex-col items-center justify-end h-full"
                >
                  <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                    <div className="bg-base-300 text-base-content text-xs rounded-selector px-2 py-1 whitespace-nowrap shadow-lg">
                      {hour}:00 — {d.count} req
                    </div>
                  </div>
                  {d.count > 0 ? (
                    <div
                      className={`w-full rounded-t-selector transition-all ${isNow ? 'bg-primary' : 'bg-primary/60'} hover:bg-primary/80`}
                      style={{ height: `${height}%` }}
                    />
                  ) : (
                    <div className="w-full border-t border-base-300" />
                  )}
                </div>
              );
            })}
          </div>
  
          {/* X-axis labels */}
          <div className="flex mt-1.5 border-t border-base-200 pt-1">
            {data.map((d) => {
              const hour = new Date(d.timestamp).getHours();
              const showLabel = hour % 6 === 0;
              return (
                <div key={d.timestamp} className="flex-1 text-center">
                  {showLabel && (
                    <span className="text-[10px] text-base-content/70">
                      {hour.toString().padStart(2, '0')}:00
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function DistributionBar({ items, getColor }: {
  items: Array<{ label: string; count: number; key: string | number }>;
  getColor: (key: string | number) => string;
}) {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex rounded-full overflow-hidden h-3">
        {items.map(item => {
          const pct = (item.count / total) * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={item.key}
              className={`${getColor(item.key)} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${item.label}: ${item.count} (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {items.map(item => {
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <div key={item.key} className="flex items-center gap-1.5 text-xs">
              <div className={`w-2 h-2 rounded-full ${getColor(item.key)}`} />
              <span className="text-base-content/80">{item.label}</span>
              <span className="font-medium">{item.count}</span>
              <span className="text-base-content/70">({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AnalyticsDashboard({ analytics, loading, onRefresh }: AnalyticsDashboardProps) {
  if (loading && !analytics) {
    return <Skeleton variant="analytics" />;
  }

  if (!analytics) return null;

  const statusItems = Object.entries(analytics.statusCodes)
    .sort(([, a], [, b]) => b - a)
    .map(([status, count]) => ({
      key: Number(status),
      label: `${status} ${getStatusLabel(Number(status))}`,
      count,
    }));

  const methodItems = Object.entries(analytics.methods)
    .sort(([, a], [, b]) => b - a)
    .map(([method, count]) => ({
      key: method,
      label: method,
      count,
    }));

  return (
    <div className="card bg-base-100 mb-6">
      <div className="p-4 border-b border-base-200 flex items-center justify-between">
        <h2 className="text-base-content">Analytics</h2>
        <button
          onClick={onRefresh}
          className="btn btn-ghost btn-xs"
          disabled={loading}
          title="Refresh analytics"
          aria-label="Refresh analytics"
        >
          <RefreshCwIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-base-200 border-b border-base-200">
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-base-content">{analytics.totalRequests.toLocaleString()}</div>
          <div className="text-xs text-base-content/70 mt-0.5">Total Requests</div>
          <TrendArrow today={analytics.requestsToday} yesterday={analytics.requestsYesterday} />
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-base-content">{analytics.avgResponseTime}<span className="text-sm font-normal text-base-content/70">ms</span></div>
          <div className="text-xs text-base-content/70 mt-0.5">Avg Response</div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-base-content">{Object.keys(analytics.methods).length}</div>
          <div className="text-xs text-base-content/70 mt-0.5">Methods Used</div>
        </div>
      </div>

      {/* Requests over time */}
      <div className="p-4 border-b border-base-200">
        <div className="text-xs text-base-content/70 mb-2">Requests (last 24h)</div>
        <BarChart data={analytics.requestsOverTime} />
      </div>

      {/* Status code distribution */}
      {statusItems.length > 0 && (
        <div className="p-4 border-b border-base-200">
          <div className="text-xs text-base-content/70 mb-2">Status Codes</div>
          <DistributionBar
            items={statusItems}
            getColor={(key) => statusColor(key as number).fill}
          />
        </div>
      )}

      {/* Method distribution */}
      {methodItems.length > 0 && (
        <div className="p-4">
          <div className="text-xs text-base-content/70 mb-2">Methods</div>
          <DistributionBar
            items={methodItems}
            getColor={(key) => methodColor(key as string).fill}
          />
        </div>
      )}
    </div>
  );
}
